import React from 'react';
import { Box, useTheme, Typography, LinearProgress } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';

const DataTable = ({
  rows,
  columns,
  loading = false,
  pageSize = 25,
  rowCount,
  page,
  onPageChange,
  onPageSizeChange,
  onRowClick,
  checkboxSelection = false,
  onSelectionChange,
  getRowId,
  emptyMessage = 'No data found',
  autoHeight = false,
  ...props
}) => {
  const theme = useTheme();

  const customStyles = {
    border: 'none',
    backgroundColor: theme.palette.background.paper,
    borderRadius: 3,
    '& .MuiDataGrid-columnHeaders': {
      backgroundColor: theme.palette.background.elevated,
      borderBottom: `1px solid ${theme.palette.divider}`,
      borderRadius: '12px 12px 0 0',
      '& .MuiDataGrid-columnHeader': {
        '&:focus, &:focus-within': {
          outline: 'none',
        },
      },
      '& .MuiDataGrid-columnHeaderTitle': {
        fontFamily: "'Space Mono', monospace",
        fontWeight: 500,
        fontSize: '0.68rem',
        textTransform: 'lowercase',
        letterSpacing: '0.03em',
        color: theme.palette.text.secondary,
      },
    },
    '& .MuiDataGrid-row': {
      '&:hover': {
        backgroundColor: theme.palette.background.hover,
        cursor: onRowClick ? 'pointer' : 'default',
      },
      '&.Mui-selected': {
        backgroundColor: `${theme.palette.primary.main}10`,
        '&:hover': {
          backgroundColor: `${theme.palette.primary.main}15`,
        },
      },
    },
    '& .MuiDataGrid-cell': {
      borderBottom: `1px solid ${theme.palette.divider}`,
      display: 'flex',
      alignItems: 'center',
      '&:focus, &:focus-within': {
        outline: 'none',
      },
    },
    '& .MuiDataGrid-footerContainer': {
      borderTop: `1px solid ${theme.palette.divider}`,
      backgroundColor: theme.palette.background.elevated,
      borderRadius: '0 0 12px 12px',
    },
    '& .MuiTablePagination-root': {
      color: theme.palette.text.secondary,
    },
    '& .MuiCheckbox-root': {
      color: theme.palette.text.disabled,
      '&.Mui-checked': {
        color: theme.palette.primary.main,
      },
    },
    '& .MuiDataGrid-overlay': {
      backgroundColor: 'transparent',
    },
  };

  const NoRowsOverlay = () => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        py: 6,
      }}
    >
      <Typography
        variant="body2"
        sx={{ color: theme.palette.text.secondary }}
      >
        {emptyMessage}
      </Typography>
    </Box>
  );

  const LoadingOverlay = () => (
    <Box sx={{ width: '100%', position: 'absolute', top: 0 }}>
      <LinearProgress
        sx={{
          '& .MuiLinearProgress-bar': {
            backgroundColor: theme.palette.primary.main,
          },
        }}
      />
    </Box>
  );

  return (
    <Box
      sx={{
        width: '100%',
        height: autoHeight ? 'auto' : 600,
        '& .MuiDataGrid-root': customStyles,
      }}
    >
      <DataGrid
        rows={rows}
        columns={columns}
        loading={loading}
        pageSizeOptions={[10, 25, 50, 100]}
        paginationModel={{ page: page || 0, pageSize: pageSize }}
        onPaginationModelChange={(model) => {
          if (onPageChange) onPageChange(model.page);
          if (onPageSizeChange) onPageSizeChange(model.pageSize);
        }}
        paginationMode={rowCount !== undefined ? 'server' : 'client'}
        rowCount={rowCount}
        onRowClick={onRowClick ? (params) => onRowClick(params.row) : undefined}
        checkboxSelection={checkboxSelection}
        onRowSelectionModelChange={onSelectionChange}
        disableRowSelectionOnClick={!checkboxSelection}
        getRowId={getRowId}
        slots={{
          noRowsOverlay: NoRowsOverlay,
          loadingOverlay: LoadingOverlay,
        }}
        autoHeight={autoHeight}
        rowHeight={52}
        disableColumnMenu
        {...props}
      />
    </Box>
  );
};

export default DataTable;
